import { useState, useMemo, useCallback, useEffect } from "react";
import { Pencil, User, Clock, Search, ChevronDown } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import ConfigDynamicTable from "@/components/BaseTable/ConfigDynamicTable";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Select, Tooltip, Dropdown, Checkbox, Input } from "antd";
import {
  DropdownService,
  DynamicModuleService,
  PtsProjectService,
} from "@/services/APIs";
import { get } from "@/services/axiosUitils";
import { getEmployeeId } from "@/lib/storeManager";
import { isEmployee, isPTSIT } from "../../utils/rolePermissions";

const TASK_LISTING_TABLE_NAME = "PTS Task Listing";
// const TASK_SCHEMA_NAME = "PTS Task";
const EMPLOYEE_SCHEMA_NAME = "PTS Emp";
const TASK_CATEGORY_SCHEMA_NAME = "PTS Task Category";
const STATUS_SCHEMA_NAME = "PTS Status";
const WORK_TYPE_SCHEMA_NAME = "PTS Work Type";

type SelectOption = { value: string; label: string };

/** Checkbox multi-select dropdown with search + footer (matches design). */
const ProjectMultiSelect = ({
  options,
  value,
  onChange,
  placeholder = "All Projects",
  className,
}: {
  options: SelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return options;
    }
    return options.filter(option =>
      String(option.label).toLowerCase().includes(term)
    );
  }, [options, search]);

  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(item => item !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const triggerLabel =
    value.length > 0 ? `${value.length} selected` : placeholder;

  const panel = (
    <div className="w-64 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
      <div className="border-b border-gray-100 p-2">
        <Input
          prefix={<Search className="h-4 w-4 text-gray-400" />}
          placeholder="Search projects..."
          value={search}
          onChange={event => setSearch(event.target.value)}
          allowClear
        />
      </div>
      <div className="max-h-60 overflow-auto py-1">
        {filteredOptions.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-gray-400">
            No projects found
          </div>
        ) : (
          filteredOptions.map(option => {
            const checked = value.includes(option.value);
            return (
              <div
                key={option.value}
                role="button"
                tabIndex={0}
                onClick={() => toggleOption(option.value)}
                onKeyDown={event => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    toggleOption(option.value);
                  }
                }}
                className={`flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${
                  checked ? "bg-gray-50" : ""
                }`}
              >
                <Checkbox checked={checked} />
                <span className="truncate text-gray-700">{option.label}</span>
              </div>
            );
          })
        )}
      </div>
      <div className="flex items-center justify-between border-t border-gray-100 px-3 py-2 text-xs">
        <span className="text-gray-500">{value.length} selected</span>
        <button
          type="button"
          className="text-primary font-medium hover:underline"
          onClick={() => onChange([])}
        >
          Clear
        </button>
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      trigger={["click"]}
      placement="bottomLeft"
      dropdownRender={() => panel}
    >
      <button
        type="button"
        className={`flex items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm hover:border-gray-400 ${
          className ?? ""
        }`}
      >
        <span
          className={`truncate ${
            value.length > 0 ? "text-gray-700" : "text-gray-400"
          }`}
        >
          {triggerLabel}
        </span>
        <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" />
      </button>
    </Dropdown>
  );
};

const EmployeeTooltipContent = ({
  userId,
}: {
  userId: string | null | undefined;
}) => {
  const { data: employeeData, isLoading } = useQuery({
    queryKey: ["employee-by-user-id", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      try {
        const modelName = encodeURIComponent(EMPLOYEE_SCHEMA_NAME);
        return await get<any>(`/formily/dynamic-model/${modelName}/`, {
          params: { users__eq: userId },
        });
      } catch {
        return null;
      }
    },
    enabled: !!userId,
  });

  const employeeName = useMemo(() => {
    if (!employeeData) {
      return "Loading...";
    }
    const results =
      employeeData?.results ??
      employeeData?.data ??
      (Array.isArray(employeeData) ? employeeData : []);
    if (results.length === 0) {
      return "Unknown Employee";
    }
    const employee = results[0];
    const firstName =
      employee.first_name ??
      employee.firstname ??
      employee.fname ??
      employee.firstName ??
      "";
    const lastName =
      employee.last_name ??
      employee.lastname ??
      employee.lname ??
      employee.lastName ??
      "";
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || "Unknown Employee";
  }, [employeeData]);

  if (isLoading) {
    return <span className="text-gray-700">Loading...</span>;
  }

  return <span className="text-gray-700">{employeeName}</span>;
};

const ServiceDeskTasksPage = () => {
  const currentEmployeeId = getEmployeeId();
  // PTS_IT matches employee behavior: no employee dropdown, scoped to self.
  const userIsEmployee = isEmployee() || isPTSIT();

  const [searchQuery] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [sprintFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [projectFilter, setProjectFilter] = useState<string[]>([]);
  const [assigneeFilter, setAssigneeFilter] = useState<string>(() =>
    userIsEmployee && currentEmployeeId ? currentEmployeeId : "all"
  );

  // PTS Employee / PTS_IT: always scope the listing to the logged-in employee
  useEffect(() => {
    if (userIsEmployee && currentEmployeeId) {
      setAssigneeFilter(currentEmployeeId);
    }
  }, [userIsEmployee, currentEmployeeId]);

  const { data: projectOptions } = useQuery({
    queryKey: ["open-project-list"],
    queryFn: () => PtsProjectService.getOpenProjectList(),
  });

  const { data: employeeOptions } = useQuery({
    queryKey: ["employee-dropdown", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(EMPLOYEE_SCHEMA_NAME),
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  const { data: employeeDataResponse } = useQuery({
    queryKey: ["employee-data", EMPLOYEE_SCHEMA_NAME],
    queryFn: () =>
      DynamicModuleService.getGridColItems(EMPLOYEE_SCHEMA_NAME, 1, 1000, {}),
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  const employeeRecords = useMemo(() => {
    if (!employeeDataResponse) {
      return [];
    }
    if (Array.isArray(employeeDataResponse)) {
      return employeeDataResponse;
    }
    if (
      employeeDataResponse.results &&
      Array.isArray(employeeDataResponse.results)
    ) {
      return employeeDataResponse.results;
    }
    return [];
  }, [employeeDataResponse]);

  const employeeOptionsWithNames = useMemo(() => {
    if (!employeeOptions || !employeeRecords.length) {
      return employeeOptions ?? [];
    }

    return employeeOptions.map((employee: { value: string; label: string }) => {
      const employeeRecord = employeeRecords.find(
        (record: any) =>
          String(
            record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? ""
          ) === String(employee.value)
      );

      if (employeeRecord) {
        const firstName =
          employeeRecord.first_name ??
          employeeRecord.firstname ??
          employeeRecord.fname ??
          "";
        const lastName =
          employeeRecord.last_name ??
          employeeRecord.lastname ??
          employeeRecord.lname ??
          "";
        const fullName = `${firstName} ${lastName}`.trim();
        return {
          value: employee.value,
          label: fullName || employee.label,
        };
      }

      return employee;
    });
  }, [employeeOptions, employeeRecords]);

  const { data: categoryOptions } = useQuery({
    queryKey: ["category-dropdown", TASK_CATEGORY_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(TASK_CATEGORY_SCHEMA_NAME),
    enabled: !!TASK_CATEGORY_SCHEMA_NAME,
  });

  const { data: workTypeOptions } = useQuery({
    queryKey: ["work-type-dropdown", WORK_TYPE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(WORK_TYPE_SCHEMA_NAME),
    enabled: !!WORK_TYPE_SCHEMA_NAME,
  });

  const { data: statusOptions } = useQuery({
    queryKey: ["status-dropdown", STATUS_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(STATUS_SCHEMA_NAME),
    enabled: !!STATUS_SCHEMA_NAME,
  });

  const getProjectNameById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id) {
        return null;
      }
      return projectOptions?.find(
        (item: { value: string; label: string }) =>
          String(item.value) === String(id)
      );
    },
    [projectOptions]
  );

  const formatDateTime = useCallback(
    (dateString: string | null | undefined) => {
      if (!dateString) {
        return "-";
      }
      try {
        const date = new Date(dateString);
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const month = monthNames[date.getMonth()];
        const day = date.getDate();
        let hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
        return `${month} ${day}, ${hours}:${minutes} ${ampm}`;
      } catch {
        return dateString;
      }
    },
    []
  );

  const filter = useMemo(() => {
    const filterObj: Record<string, string> = {};

    if (searchQuery?.trim()) {
      filterObj.taskname = searchQuery.trim();
    }
    if (sprintFilter && sprintFilter !== "all") {
      filterObj.sprint = sprintFilter;
    }
    if (statusFilter && statusFilter !== "all") {
      filterObj.status = statusFilter;
    }
    if (projectFilter && projectFilter.length > 0) {
      filterObj.project = projectFilter.join(",");
    }
    if (assigneeFilter && assigneeFilter !== "all") {
      filterObj.assignees = assigneeFilter;
    }

    return filterObj;
  }, [searchQuery, sprintFilter, statusFilter, projectFilter, assigneeFilter]);

  const columns: ProColumns<any>[] = useMemo(() => {
    // const handleDelete = async (id: string | undefined) => {
    //   if (!id) {
    //     notify.error("Task ID is missing");
    //     return;
    //   }
    //   try {
    //     const response = await DynamicModuleService.deleteItem(
    //       TASK_SCHEMA_NAME,
    //       String(id)
    //     );
    //     if (response) {
    //       queryClient.invalidateQueries({
    //         queryKey: ["moduleData", TASK_LISTING_TABLE_NAME],
    //       });
    //     }
    //   } catch (error: any) {
    //     notify.error(error?.message ?? "Failed to delete task");
    //   }
    // };

    // const handleEdit = (id: string | undefined) => {
    //   if (!id) {
    //     notify.error("Task ID is missing");
    //     return;
    //   }
    //   navigate(PTSNavigationRoutes.TASKS, {
    //     state: { editTaskId: String(id) },
    //   });
    // };

    const renderVariance = (variance: unknown) => {
      const varianceNum =
        typeof variance === "number"
          ? variance
          : typeof variance === "string"
            ? parseFloat(variance.replace(":", "."))
            : NaN;

      const displayValue =
        variance === null || variance === undefined
          ? "-"
          : typeof variance === "string" || typeof variance === "number"
            ? String(variance)
            : "-";

      if (displayValue === "-" || Number.isNaN(varianceNum)) {
        return <span className="text-gray-900">{displayValue}</span>;
      }

      if (varianceNum < 0) {
        return <span className="font-normal text-red-600">{displayValue}</span>;
      }
      if (varianceNum > 0) {
        return (
          <span className="font-normal text-green-600">{displayValue}</span>
        );
      }
      return <span className="text-gray-900">{displayValue}</span>;
    };

    return [
      {
        title: "Servicedesk ID",
        dataIndex: "servicedesk_uid",
        key: "servicedesk_uid",
        render: (_: any, record: any) => {
          if (!record.servicedesk_uid) {
            return "-";
          }
          return (
            <button
              type="button"
              className="cursor-pointer bg-transparent p-0 font-medium text-blue-700 hover:text-blue-900 hover:underline"
            >
              {record.servicedesk_uid}
            </button>
          );
        },
      },
      {
        title: "Task Name",
        dataIndex: "taskname",
        key: "task_name",
        width: 300,
        render: (_: any, record: any) => {
          const taskName = record.taskname ?? "-";
          const isEdited =
            record.created_by !== record.updated_by ||
            record.created_on !== record.updated_on;
          const formattedDate = isEdited
            ? formatDateTime(record.updated_on)
            : "";

          return (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-black">{taskName}</span>
              </div>
              {isEdited && (
                <Tooltip
                  title={
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-gray-800" />
                        <EmployeeTooltipContent userId={record.updated_by} />
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-gray-800" />
                        <span className="text-gray-700">{formattedDate}</span>
                      </div>
                    </div>
                  }
                  placement="bottom"
                  overlayInnerStyle={{
                    backgroundColor: "#ffffff",
                    color: "#374151",
                    borderRadius: "8px",
                    border: "1px solid #e0e6eb",
                    padding: "8px 12px",
                    boxShadow:
                      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                  }}
                >
                  <div className="flex cursor-pointer items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                    <Pencil size={12} />
                    <span>Edited</span>
                  </div>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: "Project",
        dataIndex: "project",
        key: "project",
        render: (_: any, record: any) => {
          const projectId = record.project_id ?? record.project;
          if (!projectId) {
            return "-";
          }
          const project = getProjectNameById(projectId);
          return project?.label ?? projectId;
        },
      },
      {
        title: "Work Area",
        dataIndex: "taskcategory",
        key: "category",
        render: (_: any, record: any) => {
          const categoryId = record.taskcategory ?? record.category;
          if (!categoryId) {
            return "-";
          }
          const category = categoryOptions?.find(
            (item: { value: string; label: string }) =>
              String(item.value) === String(categoryId)
          );
          return category?.label ?? categoryId;
        },
      },
      {
        title: "Work Type",
        dataIndex: "work_type",
        key: "work_type",
        render: (_: any, record: any) => {
          const workTypeId = record.work_type;
          if (!workTypeId) {
            return "-";
          }
          const option = workTypeOptions?.find(
            (item: {
              value?: string;
              label?: string;
              id?: string;
              name?: string;
            }) => String(item?.value ?? item?.id ?? "") === String(workTypeId)
          );
          return (option?.label ?? option?.name ?? workTypeId) as string;
        },
      },
      {
        title: "Est. Hours",
        dataIndex: "taskestimatedhours",
        key: "hours",
        render: (_: any, record: any) =>
          record.taskestimatedhours ?? record.est_hours ?? "0:00",
      },
      {
        title: "Actual Hours",
        dataIndex: "actualhours",
        key: "actualhours",
        render: (_: any, record: any) => record.actualhours ?? "0:00",
      },
      {
        title: "Variance",
        dataIndex: "variance",
        key: "variance",
        render: (_: any, record: any) => renderVariance(record.variance),
      },
      // {
      //   title: "Actions",
      //   width: 120,
      //   valueType: "option",
      //   key: "actions",
      //   fixed: "right",
      //   render: (_: any, record: any) => {
      //     const recordId =
      //       record?.id ?? record?.ID ?? record?.Id ?? record?._id;

      //     return [
      //       <CustomButton
      //         key="edit"
      //         icon={<Edit2 size={16} />}
      //         type="text"
      //         onClick={() => handleEdit(recordId)}
      //         title="Edit Task"
      //       />,
      //       <Popconfirm
      //         key="delete"
      //         title="Are you sure you want to delete this task?"
      //         onConfirm={() => handleDelete(recordId)}
      //         okText="Yes"
      //         cancelText="No"
      //       >
      //         <CustomButton
      //           danger
      //           icon={<Trash size={16} />}
      //           type="text"
      //           title="Delete Task"
      //         />
      //       </Popconfirm>,
      //     ];
      //   },
      // },
    ];
  }, [categoryOptions, formatDateTime, getProjectNameById, workTypeOptions]);

  const toolBarRender = () => [
    // <div key="search" className="relative">
    //   <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
    //   <Input
    //     type="text"
    //     placeholder="Search by task name..."
    //     value={searchQuery}
    //     onChange={e => setSearchQuery(e.target.value)}
    //     className="w-64 pl-10"
    //   />
    // </div>,
  ];

  const filterSelectProps = {
    allowClear: true as const,
    showSearch: true as const,
    filterOption: (input: string, option: { label?: string } | undefined) => {
      const label = String(option?.label ?? "");
      return label.toLowerCase().includes(input.toLowerCase());
    },
  };

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      <div>
        <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
          Service Desk Tasks
        </h1>
        <p className="text-sm text-[#627384]">
          Service desk task listing with project, status, and assignee tracking
        </p>
      </div>

      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-[#627384]">
                Project
              </label>
              <ProjectMultiSelect
                value={projectFilter}
                onChange={setProjectFilter}
                placeholder="All Projects"
                className="w-44"
                options={
                  projectOptions?.map(
                    (project: { value: string; label: string }) => ({
                      value: project.value,
                      label: project.label,
                    })
                  ) ?? []
                }
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-[#627384]">
                Status
              </label>
              <Select
                id="status"
                {...filterSelectProps}
                value={statusFilter}
                onChange={setStatusFilter}
                placeholder="All Statuses"
                className="w-44"
                options={[
                  { value: "all", label: "All Statuses" },
                  ...(statusOptions?.map(
                    (status: { value: string; label: string }) => ({
                      value: status.value,
                      label: status.label,
                    })
                  ) ?? []),
                ]}
              />
            </div>

            {!userIsEmployee && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[#627384]">
                  Employee
                </label>
                <Select
                  id="employee"
                  {...filterSelectProps}
                  value={assigneeFilter}
                  onChange={setAssigneeFilter}
                  placeholder="All Employees"
                  className="w-44"
                  options={[
                    { value: "all", label: "All Employees" },
                    ...(employeeOptionsWithNames?.map(
                      (employee: { value: string; label: string }) => ({
                        value: employee.value,
                        label: employee.label,
                      })
                    ) ?? []),
                  ]}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-4">
          <ConfigDynamicTable
            tableName={TASK_LISTING_TABLE_NAME}
            columns={columns}
            headerTitle=""
            rowKey="id"
            filter={filter}
            toolBarRender={toolBarRender}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default ServiceDeskTasksPage;
